import { Box, Button, Heading, Icon, Page, Text } from '@zillow/constellation-10';

import { PREMIUM_FEATURES_BY_CONTEXT } from '../../../../../constants/FreemiumConstants';

import MultiplexWarningBanner from '../../../../MultiplexWarningBanner';
import LearnAboutPremiumButton from './LearnAboutPremiumButton';
import PremiumProductOptionsSimple from './PremiumProductOptionsSimple';

import type { RefObject } from 'react';
import type { ActivateMultiplexTriggerObject } from '../../../../../analytics/events/payAndPublish';

type Props = {
  onPublishClick: (isPremium: boolean, multiplexTriggerObject?: ActivateMultiplexTriggerObject) => void;
  componentContext?: string;
  displayContentFullWidth?: boolean;
  hideCompareAndLearnMore?: boolean;
  offsetPageWizardTopMargin?: boolean;
  reducePaddingTop?: boolean;
  premiumCompareRef?: RefObject<HTMLDivElement>;
  isInBuildingGroup?: boolean;
};

const PremiumFeatureSummary = ({
  onPublishClick,
  displayContentFullWidth = false,
  hideCompareAndLearnMore = false,
  offsetPageWizardTopMargin = true,
  reducePaddingTop = false,
  premiumCompareRef,
  isInBuildingGroup = false,
}: Props) => {
  const gridColumnEndLeftColumn = displayContentFullWidth
    ? { base: 'span 6', lg: 'span 12' }
    : { base: 'span 6', lg: 'span 12', xxl: 'span 7' };

  const gridColumnEndRightColumn = displayContentFullWidth
    ? { base: 'span 6', lg: 'span 12' }
    : { base: 'span 6', lg: 'span 12', xxl: 'span 5' };

  const executeScroll = () => {
    premiumCompareRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box
      data-testid="PremiumFeatureSummary"
      css={{
        position: 'relative',
        marginTop: offsetPageWizardTopMargin ? '-32px' : undefined,
      }}
    >
      <Box css={{ xxlDown: { width: '90%', margin: 'auto' } }}>
        <Page.Content
          css={{
            padding: '400',
            width: '100%',
            boxSizing: 'border-box',
            margin: '0 !important',
            maxWidth: '1232px',
            paddingTop: reducePaddingTop ? '400' : '800',
          }}
        >
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: {
                base: 'repeat(6, 1fr)',
                lg: 'repeat(12, 1fr)',
              },
              gap: '400',
            }}
          >
            <Box css={{ gridColumnEnd: gridColumnEndLeftColumn }}>
              <Heading level={1} textStyle="heading-md" fontFamily="heading-alt" css={{ marginBottom: '600' }}>
                Get the most from your listing with premium
              </Heading>

              <MultiplexWarningBanner isInBuildingGroup={isInBuildingGroup} />

              <Box data-testid="PremiumUpsellDetailsContainer" css={{ marginBottom: '600' }}>
                {Object.entries(PREMIUM_FEATURES_BY_CONTEXT).map(([key, value]) => {
                  const { HEADING, BODY, ICON: IconComponent } = value;
                  return (
                    <Box key={key} css={{ display: 'flex', flexDirection: 'row', marginBottom: '600' }}>
                      <div>
                        <Icon size="lg" css={{ marginRight: '400', color: 'icon.subtle' }} render={<IconComponent />} />
                      </div>
                      <Box>
                        <Text textStyle="body-bold" asChild>
                          <p>{HEADING}</p>
                        </Text>
                        <Text asChild>
                          <p>{BODY}</p>
                        </Text>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {!hideCompareAndLearnMore && (
                <Box css={{ display: 'flex', flexDirection: 'row', marginBottom: '1200' }}>
                  <Button tone="brand" emphasis="outlined" css={{ marginRight: '800' }} onClick={executeScroll}>
                    Compare your options
                  </Button>
                  <LearnAboutPremiumButton as="TextButton">Learn more</LearnAboutPremiumButton>
                </Box>
              )}
            </Box>

            <Box css={{ gridColumnEnd: gridColumnEndRightColumn, justifyContent: 'center', marginBottom: '600' }}>
              <PremiumProductOptionsSimple onPublishClick={onPublishClick} />
            </Box>
          </Box>
        </Page.Content>
      </Box>
    </Box>
  );
};

export default PremiumFeatureSummary;
